import React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';

import {
  EkonomiskAnalysEditColumnFormValuesType,
  ColumnFaltkodType,
} from './Components/EkonomiskAnalysEditColumnForm';
import EkonomiskAnalysEditColumnTypeField from './Components/FieldComponents/EkonomiskAnalysEditColumnTypeField';
import EkonomiskAnalysEditColumnTimeIntervalField from './Components/FieldComponents/EkonomiskAnalysEditColumnTimeIntervalField';
import EkonomiskAnalysEditColumnTimeField from './Components/FieldComponents/EkonomiskAnalysEditColumnTimeField';
import EkonomiskAnalysEditColumnBudgetField from './Components/FieldComponents/EkonomiskAnalysEditColumnBudgetField';
import EkonomiskAnalysEditColumnCheckboxField from './Components/FieldComponents/EkonomiskAnalysEditColumnCheckboxField';
import EkonomiskAnalysEditColumnUnitField from './Components/FieldComponents/EkonomiskAnalysEditColumnUnitField';
import {
  showUnit,
  showBudget,
  findStandardColumnByFaltkod,
  showTimeInterval,
  fromToTime,
  showFromPeriod,
  showTomAr,
  showTomPeriod,
  showTimeSettings,
  getStandardColumnTitle,
  getStandardColumnSubTitle,
} from './Utils/EkonomiskAnalysEditColumnUtils';
import EkonomiskAnalysStandardColumnQuery, {
  StandardColumnType,
} from './Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import EkonomiskAnalysEditColumnBudgetRadioGroup from './Components/FieldComponents/EkonomiskAnalysEditColumnBudgetRadioGroup';
import { SetFieldValueType } from '../../../../../../../../../../Types/FormTypes';
import { ekanalys_ekanalys_columns } from '../../../../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { INewColumnData } from './Components/EkonomiskAnalysEditColumnDialog/EkonomiskAnalysEditColumnDialog';
import { columnCanBeRullande } from '../../../../../Utils/analysUtils';
import EkonomiskAnalysEditColumnColumnTitle from './Components/FieldComponents/EkonomiskAnalysEditColumnColumnTitle';
import { editColumnFormValuesToColumn } from './Utils/EkonomiskAnalysConvertEditColumnFormValuesToColumn';

const styles = (theme: Theme) => ({
  flex: {
    display: 'flex',
  },
  hidden: {
    visibility: 'hidden',
  },
  spaceBetween: {
    justifyContent: 'spaceBetween',
  },
  originAndTitleContainer: {
    marginRight: theme.spacing(2),
    flex: '1 1 auto',
  },
  typeAndUnitContainer: {
    minWidth: '130px',
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
});

interface IProps {
  formValues: EkonomiskAnalysEditColumnFormValuesType;
  setFieldValue: SetFieldValueType;
  initialColumn: ekanalys_ekanalys_columns;
  newColumnData: INewColumnData;
  classes?: any;
}

/**
 * Vyn för editeringsdialogen för en kolumn
 */
export class EkonomiskAnalysEditColumn extends React.Component<IProps> {
  onStandardColumnChange =
    (
      onChange: (newValue: string | null) => void,
      setFieldValue: SetFieldValueType,
      standardColumns: StandardColumnType[],
      formValues: EkonomiskAnalysEditColumnFormValuesType
    ) =>
    (newValue: string | null) => {
      const standardColumn = findStandardColumnByFaltkod(
        newValue as ColumnFaltkodType,
        standardColumns
      );
      setFieldValue('title', getStandardColumnTitle(standardColumn));
      setFieldValue(
        'subTitle',
        getStandardColumnSubTitle(
          standardColumn,
          formValues.fromPeriodChecked,
          formValues.tomPeriodChecked
        )
      );

      if (
        (standardColumn &&
          standardColumn.TOMAR &&
          standardColumn.TOMPER &&
          formValues.fromPeriodChecked &&
          formValues.tomPeriodChecked) ||
        formValues.timeInterval === 1
      ) {
        // Detta är en period
        setFieldValue('rullande', formValues.rullande);
      } else {
        setFieldValue('rullande', false);
      }

      if (standardColumn && standardColumn.INGAENDE) {
        setFieldValue('tomPeriod', formValues.fromPeriod);
      }

      onChange(newValue);
    };

  render() {
    return (
      <EkonomiskAnalysStandardColumnQuery>
        {({ standardColumns }) => {
          const selectedColumnOrigin = findStandardColumnByFaltkod(
            this.props.formValues.columnOrigin,
            standardColumns
          );

          const column =
            standardColumns != null
              ? editColumnFormValuesToColumn(
                  this.props.formValues,
                  this.props.initialColumn,
                  standardColumns || [],
                  this.props.newColumnData.dates
                )
              : undefined;

          return (
            <div>
              <div
                className={classNames(
                  this.props.classes.section,
                  this.props.classes.flex
                )}
              >
                <div className={this.props.classes.originAndTitleContainer}>
                  <EkonomiskAnalysEditColumnColumnTitle
                    standardColumns={standardColumns}
                    formValues={this.props.formValues}
                    setFieldValue={this.props.setFieldValue}
                    initialColumn={this.props.initialColumn}
                    newColumnData={this.props.newColumnData}
                    column={column}
                  />
                </div>
                <div className={this.props.classes.typeAndUnitContainer}>
                  <EkonomiskAnalysEditColumnTypeField />
                  <EkonomiskAnalysEditColumnUnitField
                    hidden={!showUnit(this.props.formValues.columnType)}
                  />
                </div>
              </div>

              <div className={this.props.classes.section}>
                <EkonomiskAnalysEditColumnTimeIntervalField
                  hidden={!showTimeInterval(selectedColumnOrigin)}
                />
                <div
                  className={classNames(this.props.classes.flex, {
                    [this.props.classes.hidden]: !showTimeSettings(
                      this.props.formValues.timeInterval
                    ),
                  })}
                >
                  <EkonomiskAnalysEditColumnTimeField
                    name="fromAr"
                    label={fromToTime(selectedColumnOrigin) ? 'From år' : 'År'}
                    rightMargin
                  />
                  {showFromPeriod(selectedColumnOrigin) && (
                    <EkonomiskAnalysEditColumnTimeField
                      name="fromPeriod"
                      label="From period"
                      spacedRightMargin
                    />
                  )}
                  {showTomAr(selectedColumnOrigin) && (
                    <EkonomiskAnalysEditColumnTimeField
                      name="tomAr"
                      label="Tom år"
                      rightMargin
                    />
                  )}
                  {showTomPeriod(selectedColumnOrigin) && (
                    <EkonomiskAnalysEditColumnTimeField
                      name="tomPeriod"
                      label={
                        fromToTime(selectedColumnOrigin)
                          ? 'Tom period'
                          : 'Period'
                      }
                    />
                  )}
                </div>
              </div>
              <div className={this.props.classes.section}>
                <EkonomiskAnalysEditColumnCheckboxField
                  name="flipSign"
                  label="Vänd tecken"
                />
                {column && columnCanBeRullande(column, standardColumns) && (
                  <EkonomiskAnalysEditColumnCheckboxField
                    name="rullande"
                    label="Rullande"
                  />
                )}
              </div>
              <div
                className={classNames(this.props.classes.section, {
                  [this.props.classes.hidden]:
                    !showBudget(selectedColumnOrigin),
                })}
              >
                <EkonomiskAnalysEditColumnBudgetRadioGroup />
                <EkonomiskAnalysEditColumnBudgetField
                  hidden={false}
                  budgetType={this.props.formValues.budgetType}
                />
              </div>
            </div>
          );
        }}
      </EkonomiskAnalysStandardColumnQuery>
    );
  }
}

export default withStyles(EkonomiskAnalysEditColumn, styles);
